import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Event } from 'nostr-tools';
import { parseDictionaryContent } from '../../lib/parser';
import { useNostrProfile } from '../../hooks/useNostrProfile';
import { getAvatarUrl } from '../../lib/avatarUtils';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X as CloseIcon } from 'lucide-react';

interface RoomMessageBubbleProps {
    event: Event;
    isOwnMessage: boolean;
}

export function RoomMessageBubble({ event, isOwnMessage }: RoomMessageBubbleProps) {
    const { metadata } = useNostrProfile(event.pubkey);
    const [expandedImage, setExpandedImage] = useState<string | null>(null);

    const displayName = metadata?.display_name || metadata?.name || `${event.pubkey.slice(0, 8)}...`;
    const avatarUrl = metadata?.picture || getAvatarUrl(displayName);

    // Image URL detection
    const imageRegex = /(https?:\/\/[^\s]+?\.(?:jpg|jpeg|png|webp|gif|svg)|https?:\/\/blossom[^\s]+)/gi;
    const imageUrls = Array.from(event.content.matchAll(imageRegex)).map(match => match[0]);
    const textContent = event.content.replace(imageRegex, '').trim();
    const isImageOnly = imageUrls.length > 0 && textContent === '';

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4 gap-3`}
        >
            {!isOwnMessage && (
                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 mt-1 shadow-sm border border-surface-200 dark:border-surface-700">
                    <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                </div>
            )}

            <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {!isOwnMessage && (
                    <span className="text-[10px] font-poppins font-bold text-mavi-500 dark:text-mavi-400 uppercase tracking-widest ml-1">
                        {displayName}
                    </span>
                )}

                <div
                    className={`relative rounded-2xl transition-all ${isOwnMessage
                        ? isImageOnly ? 'bg-transparent' : 'bg-gradient-to-br from-mavi-500 to-mavi-600 text-white rounded-br-md px-4 py-2.5 shadow-md shadow-mavi-500/10'
                        : isImageOnly ? 'bg-transparent' : 'bg-surface-100 dark:bg-surface-800 text-gray-900 dark:text-white rounded-bl-md px-4 py-2.5 border border-surface-200 dark:border-surface-700'
                        }`}
                >
                    {textContent && (
                        <div
                            className="font-merriweather text-sm whitespace-pre-wrap break-words prose prose-sm dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: parseDictionaryContent(textContent) }}
                        />
                    )}

                    {imageUrls.map((url, idx) => (
                        <div key={idx} className={`${textContent ? 'mt-2' : ''}`}>
                            <button
                                onClick={() => setExpandedImage(url)}
                                className="block w-full rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-zoom-in"
                                style={{ maxWidth: '100%', width: 'fit-content' }}
                            >
                                <img src={url} alt="Shared" className="max-w-full max-h-80 object-contain" />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-2 px-1">
                    <span className="text-[9px] text-gray-400 dark:text-gray-500 font-poppins uppercase tracking-tighter">
                        {formatDistanceToNow(event.created_at * 1000, {
                            addSuffix: true,
                            locale: tr,
                        })}
                    </span>
                </div>
            </div>

            {expandedImage && createPortal(
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setExpandedImage(null)}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 cursor-zoom-out"
                >
                    <button className="absolute top-6 right-6 p-2 bg-white/10 rounded-full text-white">
                        <CloseIcon className="w-6 h-6" />
                    </button>
                    <img src={expandedImage} alt="Full size" className="max-w-full max-h-full object-contain rounded-lg" />
                </motion.div>,
                document.body
            )}
        </motion.div>
    );
}
